import React, { createElement, Component } from 'react';
import PropTypes from 'prop-types';
import isReactNative from '@insight/toolkit-utils/lib/types/environment/isReactNative';

import { fieldSubscriptionItems } from '../constants';
import { defaultFormat, diffSubscription, getValue } from '../helpers';
import ComponentDecorator from '../Components/ComponentDecorator';
import renderComponent from '../renderComponent';

const all = fieldSubscriptionItems.reduce(
  (result, key) => ({
    ...result,
    [key]: true,
  }),
  {}
);

export default class ConnectedField extends Component {
  constructor(props) {
    super(props);

    let initialState;
    if (props.form) {
      this.subscribe(props, (state) => {
        if (initialState) {
          this.notify(state);
        } else {
          initialState = state;
        }
      });
    }

    this.state = { state: initialState };
  }

  componentDidUpdate(prevProps) {
    const { form, name, subscription, validate } = this.props;
    const diff = diffSubscription(
      prevProps.subscription,
      subscription,
      fieldSubscriptionItems
    );
    if (
      prevProps.name !== name ||
      diff ||
      prevProps.validate !== this.props.validate
    ) {
      if (form) {
        this.unsubscribe();
        this.subscribe(this.props, this.notify);
      }
    }
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  subscribe = ({ isEqual, name, subscription, validateFields }, listener) => {
    const { form, required, validate } = this.props;
    this.unsubscribe = form.registerField(name, listener, subscription || all, {
      getValidator: () => validate,
      isEqual,
      validateFields,
    });
  };

  notify = (state) => this.setState({ state });

  onBlur = (e) => {
    const { state } = this.state;
    if (state) {
      const { format, formatOnBlur } = this.props;
      state.blur();
      if (format && formatOnBlur) {
        state.change(format(state.value, state.name));
      }
    }
  };

  onChange = (e) => {
    const { state } = this.state;
    const { parse, value: valueProp } = this.props;
    const value =
      e && e.target
        ? getValue(e, state && state.value, valueProp, isReactNative)
        : e;
    state && state.change(parse ? parse(value, this.props.name) : value);
  };

  onFocus = (e) => {
    this.state.state && this.state.state.focus();
  };

  renderComponent = (formState) => {
    const {
      allowNull,
      component,
      children,
      fieldComponent,
      form,
      format,
      formatOnBlur,
      isEqual,
      name,
      parse,
      required,
      subscription,
      validate,
      validateFields,
      value: valueProp,
      when,
      ...rest
    } = this.props;

    let {
      blur,
      change,
      focus,
      name: ignoreName,
      value,
      ...otherState
    } = this.state.state || {};

    if (formatOnBlur) {
      value = defaultFormat(value, name);
    } else if (format) {
      value = format(value, name);
    }

    if (value === null && !allowNull) {
      value = '';
    }

    let isRequired = required;
    if (typeof required === 'function') {
      const formState = formState || form.getState();
      isRequired = required({ values: formState.values });
    }

    const fieldProps = {
      name,
      onBlur: this.onBlur,
      onChange: this.onChange,
      onFocus: this.onFocus,
      required: isRequired,
      value,
      ...rest,
    };

    if (
      rest.type === 'checkbox' ||
      fieldComponent === 'Checkbox' ||
      fieldComponent === 'CheckboxGroup'
    ) {
      if (valueProp === undefined) {
        fieldProps.checked = !!value;
      } else {
        fieldProps.checked = !!(
          Array.isArray(value) && ~value.indexOf(valueProp)
        );
        fieldProps.value = valueProp;
      }
    } else if (
      rest.type === 'radio' ||
      fieldComponent === 'Radio' ||
      fieldComponent === 'RadioGroup'
    ) {
      fieldProps.checked = value === valueProp;
      fieldProps.value = value;
    } else if (
      (component === 'select' || fieldComponent === 'Select') &&
      rest.multiple
    ) {
      fieldProps.value = fieldProps.value || {};
    }

    if (typeof children === 'function') {
      return children({ fieldProps, meta: otherState });
    }
    if (typeof component === 'string') {
      return createElement(component, {
        ...fieldProps,
        meta: otherState,
        children,
      });
    }

    if (typeof fieldComponent === 'string') {
      fieldProps.errors =
        otherState.error !== undefined ? [otherState.error] : [];
      return (
        <ComponentDecorator
          fieldComponent={fieldComponent}
          fieldProps={fieldProps}
        />
      );
    }

    const renderProps = { fieldProps, meta: otherState };
    return renderComponent(
      { ...renderProps, children, component },
      `Field ${name}`
    );
  };

  render() {
    const { form, when } = this.props;
    if (when && typeof when === 'function') {
      const formState = form.getState();
      return when({ values: formState.values })
        ? this.renderComponent(formState)
        : null;
    }

    return this.renderComponent();

    const renderProps = { fieldProps, meta: otherState };
    return renderComponent(
      { ...renderProps, children, component },
      `Field(${name})`
    );
  }
}

ConnectedField.propTypes = {
  allowNull: PropTypes.bool,
  component: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  children: PropTypes.func,
  fieldComponent: PropTypes.string,
  form: PropTypes.shape({}),
  format: PropTypes.func,
  formatOnBlur: PropTypes.bool,
  parse: PropTypes.func,
  isEqual: PropTypes.func,
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string.isRequired,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  showCounter: PropTypes.bool,
  subscription: PropTypes.shape({}),
  validate: PropTypes.func,
  validateFields: PropTypes.arrayOf([PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  when: PropTypes.func,
};
